/**
 * Diagnostics for video calls: keeps a short in-memory history of Chime SDK
 * logs and snapshots the browser's audio playback state, so a problem
 * reported to the backend arrives with enough context to explain it.
 */

import { ConsoleLogger, LogLevel } from "amazon-chime-sdk-js";

import { REPORT_MESSAGE_MAX_BYTES } from "features/comunications/hooks/useVideoMeetingApi";
import { byteLength, truncateToBytes } from "utils/textBytes";

const LOG_BUFFER_CAPACITY = 200;
const LOG_LINES_PER_REPORT = 40;
const MEETING_HISTORY_ENTRIES_PER_REPORT = 20;
// Some Chime log lines dump whole connection descriptions (several KB). Capped
// as they're recorded, so one line can't push all the others out of a report.
const MAX_LOG_LINE_BYTES = 500;

const DIAGNOSTICS_URL_PARAMETER = "log";

// On unless the build sets REACT_APP_VIDEO_DIAGNOSTICS=false. ?log=true on the
// page URL still forces it on, for chasing a problem where it's switched off.
export const isDiagnosticsReportingEnabled = () => {
  const isDisabledForBuild = process.env.REACT_APP_VIDEO_DIAGNOSTICS === "false";
  const isForcedByUrl =
    new URLSearchParams(window.location.search).get(DIAGNOSTICS_URL_PARAMETER) ===
    "true";

  return !isDisabledForBuild || isForcedByUrl;
};

// Reported without logs, so failing calls can be compared with ones that worked.
export const CHIME_MILESTONE_EVENTS = new Set([
  "meetingStartSucceeded",
  "meetingReconnected",
]);

// Reported with the recent SDK logs attached.
export const CHIME_PROBLEM_EVENTS = new Set([
  "meetingStartFailed",
  "meetingFailed",
  "audioInputFailed",
  "videoInputFailed",
  "signalingDropped",
  "receivingAudioDropped",
  "sendingAudioFailed",
  "deviceLabelTriggerFailed",
]);

/**
 * Chime Logger that prints to the console at `consoleLevel` and remembers
 * everything at `bufferLevel` and above, so INFO lines are available to a
 * report without flooding the console.
 */
export class BufferedChimeLogger {
  constructor(name, { consoleLevel = LogLevel.WARN, bufferLevel = LogLevel.INFO } = {}) {
    this.consoleLogger = new ConsoleLogger(name, consoleLevel);
    this.bufferLevel = bufferLevel;
    this.entries = [];
  }

  record(level, message) {
    if (level < this.bufferLevel) return;

    const entry = `${new Date().toISOString()} [${LogLevel[level]}] ${message}`;
    this.entries.push(truncateToBytes(entry, MAX_LOG_LINE_BYTES));
    if (this.entries.length > LOG_BUFFER_CAPACITY) {
      this.entries.shift();
    }
  }

  debug(debugFunction) {
    if (LogLevel.DEBUG < this.bufferLevel) return;

    const message = typeof debugFunction === "function" ? debugFunction() : debugFunction;
    this.record(LogLevel.DEBUG, message);
    this.consoleLogger.debug(message);
  }

  info(message) {
    this.record(LogLevel.INFO, message);
    this.consoleLogger.info(message);
  }

  warn(message) {
    this.record(LogLevel.WARN, message);
    this.consoleLogger.warn(message);
  }

  error(message) {
    this.record(LogLevel.ERROR, message);
    this.consoleLogger.error(message);
  }

  // The SDK skips building messages below this level, so report the lower
  // of the two levels to keep INFO lines flowing into the buffer.
  getLogLevel() {
    return Math.min(this.bufferLevel, this.consoleLogger.getLogLevel());
  }

  setLogLevel(level) {
    this.consoleLogger.setLogLevel(level);
  }

  recentEntries(count = LOG_LINES_PER_REPORT) {
    return this.entries.slice(-count);
  }
}

export const describeAudioPlayback = (audioElement, audioContext) => {
  const stream = audioElement?.srcObject;
  const audioTracks = stream?.getAudioTracks?.() || [];

  return {
    hasElement: !!audioElement,
    hasStream: !!stream,
    paused: audioElement?.paused,
    muted: audioElement?.muted,
    volume: audioElement?.volume,
    readyState: audioElement?.readyState,
    tracks: audioTracks.map((track) => ({
      readyState: track.readyState,
      muted: track.muted,
      enabled: track.enabled,
    })),
    audioContextState: audioContext?.state || "none",
    userActivation: navigator.userActivation
      ? {
          isActive: navigator.userActivation.isActive,
          hasBeenActive: navigator.userActivation.hasBeenActive,
        }
      : "unsupported",
    visibilityState: document.visibilityState,
  };
};

export const getMicrophonePermissionState = async () => {
  try {
    const status = await navigator.permissions.query({ name: "microphone" });

    return status.state;
  } catch (_error) {
    // Firefox and older Safari don't support querying the microphone permission.
    return "unknown";
  }
};

export const describeError = (error) =>
  error ? { name: error.name, message: error.message } : null;

export const summariseChimeEventAttributes = (attributes = {}) => {
  const { meetingHistory = [], ...rest } = attributes;

  return {
    ...rest,
    meetingHistory: meetingHistory
      .slice(-MEETING_HISTORY_ENTRIES_PER_REPORT)
      .map(({ name, timestampMs }) => ({ name, timestampMs })),
  };
};

// Room for {"truncated":true,"originalBytes":…,"preview":""} around a preview.
const PREVIEW_WRAPPER_BYTES = 80;
const PREVIEW_SHRINK_STEP_BYTES = 500;

// Still valid JSON, so the backend can parse it: the oversized message becomes
// a string preview. Quotes in the preview get escaped and grow, so it shrinks
// until the whole thing fits.
const toTruncatedPreview = (message) => {
  const wrap = (previewBytes) =>
    JSON.stringify({
      truncated: true,
      originalBytes: byteLength(message),
      preview: truncateToBytes(message, previewBytes),
    });

  let previewBytes = REPORT_MESSAGE_MAX_BYTES - PREVIEW_WRAPPER_BYTES;
  let wrapped = wrap(previewBytes);
  while (byteLength(wrapped) > REPORT_MESSAGE_MAX_BYTES && previewBytes > 0) {
    previewBytes -= PREVIEW_SHRINK_STEP_BYTES;
    wrapped = wrap(Math.max(previewBytes, 0));
  }

  return wrapped;
};

/**
 * The report's errorMessage: JSON, at most REPORT_MESSAGE_MAX_BYTES of UTF-8.
 * The oldest log lines go first, then all of them; only if the details alone
 * are still too big does it fall back to a truncated preview.
 */
export const buildReportMessage = (details, recentLogs = []) => {
  const message = JSON.stringify({ ...details, recentLogs });
  if (byteLength(message) <= REPORT_MESSAGE_MAX_BYTES) return message;

  if (recentLogs.length > 0) {
    return buildReportMessage(
      details,
      recentLogs.slice(Math.ceil(recentLogs.length / 2))
    );
  }

  return toTruncatedPreview(message);
};

// Chime reports metrics about once a second; these thresholds turn samples
// into "seconds with a problem" in the call summary.
const POOR_AUDIO_LOSS_FRACTION = 0.15;
const AUDIBLE_LEVEL = 0.01;
const BITS_PER_KILOBIT = 1000;
const MILLISECONDS_PER_SECOND = 1000;

const createRunningStat = () => ({ total: 0, count: 0, lowest: null, highest: null });

const addSample = (stat, value) => {
  if (typeof value !== "number" || Number.isNaN(value)) return stat;

  return {
    total: stat.total + value,
    count: stat.count + 1,
    lowest: stat.lowest === null ? value : Math.min(stat.lowest, value),
    highest: stat.highest === null ? value : Math.max(stat.highest, value),
  };
};

const round = (value, decimals = 0) =>
  value === null ? null : Number(value.toFixed(decimals));

const describeStat = (stat, { scale = 1, decimals = 0, worst = "highest" } = {}) =>
  stat.count === 0
    ? null
    : {
        average: round((stat.total / stat.count) * scale, decimals),
        worst: round(stat[worst] * scale, decimals),
      };

/**
 * Accumulates Chime's per-second metrics into one summary per call, so every
 * call — not only failing ones — leaves a record of how it went (KIN-2012).
 */
export class CallStatsCollector {
  constructor() {
    this.startedAt = Date.now();
    this.samples = 0;
    this.secondsWithPoorAudio = 0;
    this.secondsWithIncomingAudio = 0;
    this.secondsWithOutgoingAudio = 0;
    this.secondsSendingVideo = 0;
    this.reconnects = 0;
    this.localVideoSeen = false;
    this.remoteVideoSeen = false;
    this.lastStopReason = null;
    this.stats = {
      receivedAudioLoss: createRunningStat(),
      sentAudioLossPercent: createRunningStat(),
      roundTripTimeMs: createRunningStat(),
      downstreamJitterMs: createRunningStat(),
      incomingBitrate: createRunningStat(),
      outgoingBitrate: createRunningStat(),
    };
  }

  record(metrics = {}) {
    this.samples += 1;
    this.stats = {
      receivedAudioLoss: addSample(
        this.stats.receivedAudioLoss,
        metrics.audioPacketsReceivedFractionLoss
      ),
      sentAudioLossPercent: addSample(
        this.stats.sentAudioLossPercent,
        metrics.audioPacketLossPercent
      ),
      roundTripTimeMs: addSample(this.stats.roundTripTimeMs, metrics.currentRoundTripTimeMs),
      downstreamJitterMs: addSample(
        this.stats.downstreamJitterMs,
        metrics.audioDownstreamJitterMs
      ),
      incomingBitrate: addSample(this.stats.incomingBitrate, metrics.availableIncomingBitrate),
      outgoingBitrate: addSample(this.stats.outgoingBitrate, metrics.availableOutgoingBitrate),
    };

    if (metrics.audioPacketsReceivedFractionLoss > POOR_AUDIO_LOSS_FRACTION) {
      this.secondsWithPoorAudio += 1;
    }
    if (metrics.audioDownstreamLevel > AUDIBLE_LEVEL) this.secondsWithIncomingAudio += 1;
    if (metrics.audioUpstreamLevel > AUDIBLE_LEVEL) this.secondsWithOutgoingAudio += 1;
    if (metrics.videoUpstreamBitrate > 0) this.secondsSendingVideo += 1;
  }

  noteVideoTile({ isLocal }) {
    if (isLocal) {
      this.localVideoSeen = true;
    } else {
      this.remoteVideoSeen = true;
    }
  }

  noteReconnect() {
    this.reconnects += 1;
  }

  noteStop(reason) {
    this.lastStopReason = reason;
  }

  summarise() {
    const kilobits = 1 / BITS_PER_KILOBIT;

    return {
      durationSeconds: Math.round((Date.now() - this.startedAt) / MILLISECONDS_PER_SECOND),
      metricSamples: this.samples,
      receivedAudioLossPercent: describeStat(this.stats.receivedAudioLoss, {
        scale: 100,
        decimals: 1,
      }),
      sentAudioLossPercent: describeStat(this.stats.sentAudioLossPercent, { decimals: 1 }),
      roundTripTimeMs: describeStat(this.stats.roundTripTimeMs),
      downstreamJitterMs: describeStat(this.stats.downstreamJitterMs),
      availableIncomingKbps: describeStat(this.stats.incomingBitrate, {
        scale: kilobits,
        worst: "lowest",
      }),
      availableOutgoingKbps: describeStat(this.stats.outgoingBitrate, {
        scale: kilobits,
        worst: "lowest",
      }),
      secondsWithPoorAudio: this.secondsWithPoorAudio,
      secondsWithIncomingAudio: this.secondsWithIncomingAudio,
      secondsWithOutgoingAudio: this.secondsWithOutgoingAudio,
      secondsSendingVideo: this.secondsSendingVideo,
      localVideoSeen: this.localVideoSeen,
      remoteVideoSeen: this.remoteVideoSeen,
      reconnects: this.reconnects,
      lastStopReason: this.lastStopReason,
    };
  }
}
